// Sihirbaz pencereleri: eksiklik seçimi, yeni firma, yeni personel, operatör belgesi seçimi
import { Button } from '../../ui/button';
import { DuplicateTcNotice, useDuplicateTc } from './DuplicateTcNotice';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Truck, X } from 'lucide-react';
import { COMPANY_DOCS, HAZARD_CLASSES } from '../../companies/constants';
import { EMPLOYEE_DOCS } from '../../employees/constants';
import { NationalityToggle, cleanPassport } from '../../employees/Nationality';

export const DeficiencyDialog = ({ showDeficiencyDialog, setShowDeficiencyDialog, deficiencyContext, selectedDeficiencies, setSelectedDeficiencies, getTemplatesForDoc, toggleDeficiency, handleDeficiencySave }) => (
  <Dialog open={showDeficiencyDialog} onOpenChange={(open) => {
    setShowDeficiencyDialog(open);
    if (!open) setSelectedDeficiencies([]);
  }}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          Eksiklik Seçin - {
            deficiencyContext.type === 'company' 
              ? COMPANY_DOCS.find(d => d.key === deficiencyContext.docKey)?.label 
              : EMPLOYEE_DOCS.find(d => d.key === deficiencyContext.docKey)?.label
          }
        </DialogTitle>
      </DialogHeader>
      <div className="py-4">
        <div className="space-y-2">
          {getTemplatesForDoc(deficiencyContext.docKey).length === 0 ? (
            <p className="text-slate-500 text-center py-4">
              Bu belge türü için henüz eksiklik şablonu eklenmemiş.
              <br />
              <span className="text-sm">Eksiklik Şablonları sayfasından ekleyebilirsiniz.</span>
            </p>
          ) : (
            getTemplatesForDoc(deficiencyContext.docKey).map((template) => (
              <label
                key={template.id}
                className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                  selectedDeficiencies.includes(template.template_text)
                    ? 'border-rose-300 bg-rose-50'
                    : 'border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                }`}
              >
                <Checkbox
                  checked={selectedDeficiencies.includes(template.template_text)}
                  onCheckedChange={() => toggleDeficiency(template.template_text)}
                />
                <span className="flex-1">{template.template_text}</span>
              </label>
            ))
          )}
        </div>
        {selectedDeficiencies.length > 0 && (
          <div className="mt-4 p-3 bg-rose-50 rounded-lg border border-rose-200">
            <p className="text-sm text-rose-700 font-medium">Seçilen eksiklikler ({selectedDeficiencies.length}):</p>
            <p className="text-sm text-rose-600 mt-1">{selectedDeficiencies.join(', ')}</p>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => {
          setShowDeficiencyDialog(false);
          setSelectedDeficiencies([]);
        }}>
          İptal
        </Button>
        <Button 
          onClick={handleDeficiencySave}
          disabled={selectedDeficiencies.length === 0}
          className="bg-rose-600 hover:bg-rose-700"
        >
          <X className="h-4 w-4 mr-2" />
          Eksik Kaydet ({selectedDeficiencies.length})
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const NewCompanyDialog = ({ newCompanyDialog, setNewCompanyDialog, newCompany, setNewCompany, handleAddCompany }) => (
  <Dialog open={newCompanyDialog} onOpenChange={setNewCompanyDialog}>
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Yeni Firma Ekle</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Firma Adı *</Label>
          <Input value={newCompany.name} onChange={(e) => setNewCompany(prev => ({ ...prev, name: e.target.value }))} />
        </div>
        <div>
          <Label>Tehlike Sınıfı</Label>
          <Select value={newCompany.hazard_class} onValueChange={(val) => setNewCompany(prev => ({ ...prev, hazard_class: val }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {HAZARD_CLASSES.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Yetkili Adı</Label>
            <Input value={newCompany.contact_name} onChange={(e) => setNewCompany(prev => ({ ...prev, contact_name: e.target.value }))} />
          </div>
          <div>
            <Label>Yetkili Soyadı</Label>
            <Input value={newCompany.contact_surname} onChange={(e) => setNewCompany(prev => ({ ...prev, contact_surname: e.target.value }))} />
          </div>
        </div>
        <div>
          <Label>E-posta</Label>
          <Input type="email" value={newCompany.contact_email} onChange={(e) => setNewCompany(prev => ({ ...prev, contact_email: e.target.value }))} />
        </div>
        <div>
          <Label>Telefon</Label>
          <Input value={newCompany.contact_phone} onChange={(e) => setNewCompany(prev => ({ ...prev, contact_phone: e.target.value }))} />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setNewCompanyDialog(false)}>İptal</Button>
        <Button onClick={handleAddCompany}>Ekle</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const NewEmployeeDialog = ({ newEmployeeDialog, setNewEmployeeDialog, newEmployee, setNewEmployee, handleAddEmployee, onBehalfOf, companyId, selectedIds = [], onSelectExisting }) => {
  const dup = useDuplicateTc(newEmployeeDialog ? companyId : null, newEmployee.tc_kimlik_no, newEmployee.nationality);
  return (
  <Dialog open={newEmployeeDialog} onOpenChange={setNewEmployeeDialog}>
    <DialogContent>
      <DialogHeader><DialogTitle>Yeni Personel Ekle</DialogTitle></DialogHeader>
      {onBehalfOf?.trim() && <p className="text-sm text-sky-700 bg-sky-50 rounded px-3 py-2" data-testid="new-employee-behalf-note">Bu personel <b>{onBehalfOf}</b> adına kaydedilecek</p>}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Ad *</Label><Input value={newEmployee.name} onChange={(e) => setNewEmployee(prev => ({ ...prev, name: e.target.value }))} /></div>
          <div><Label>Soyad *</Label><Input value={newEmployee.surname} onChange={(e) => setNewEmployee(prev => ({ ...prev, surname: e.target.value }))} /></div>
        </div>
        <div className="space-y-2">
          <NationalityToggle value={newEmployee.nationality || 'tc'} testPrefix="new-employee-nationality" onChange={(v) => setNewEmployee(prev => ({ ...prev, nationality: v, tc_kimlik_no: '' }))} />
          <Label className="block">{newEmployee.nationality === 'yabanci' ? 'Pasaport No *' : 'T.C. Kimlik No'}</Label>
          <Input value={newEmployee.tc_kimlik_no} data-testid="new-employee-idno-input" maxLength={newEmployee.nationality === 'yabanci' ? 30 : 11}
            placeholder={newEmployee.nationality === 'yabanci' ? 'Pasaport numarası' : '11 haneli T.C. Kimlik No'}
            onChange={(e) => setNewEmployee(prev => ({ ...prev, tc_kimlik_no: prev.nationality === 'yabanci' ? cleanPassport(e.target.value) : e.target.value.replace(/\D/g, '').slice(0, 11) }))} />
        </div>
        {dup && <DuplicateTcNotice dup={dup} isPassport={newEmployee.nationality === 'yabanci'} inPermit={selectedIds.includes(dup.id)} onSelect={() => onSelectExisting?.(dup)} />}
        <div><Label>Görevi</Label><Input placeholder="örn: Elektrikçi, Kaynakçı" value={newEmployee.position} onChange={(e) => setNewEmployee(prev => ({ ...prev, position: e.target.value }))} /></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setNewEmployeeDialog(false)}>İptal</Button>
        <Button onClick={handleAddEmployee} disabled={!newEmployee.name || !newEmployee.surname || !!dup} data-testid="new-employee-submit-btn">Ekle</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export const OperatorSelectorDialog = ({ showOperatorSelector, closeOperatorSelector, operatorSelectorStatus, operatorSelectorForEquipment, operatorSearchQuery, setOperatorSearchQuery, operatorList, selectedOperatorObj, setSelectedOperatorObj, operatorValidityDate, setOperatorValidityDate, handleOperatorSelection }) => {
  const filtered = operatorList.filter(op => 
    !operatorSearchQuery || op.name?.toLowerCase().includes(operatorSearchQuery.toLowerCase())
  );
  return (
    <Dialog open={showOperatorSelector} onOpenChange={(open) => {
      if (!open) closeOperatorSelector();
    }}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Operatörlük Belgesi Seçimi
            {operatorSelectorStatus === 'onay' && <Badge className="bg-emerald-100 text-emerald-700">Onay</Badge>}
            {operatorSelectorStatus === 'eksik' && <Badge className="bg-rose-100 text-rose-700">Eksik</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {operatorSelectorForEquipment && (
            <div className="p-3 bg-slate-50 rounded-lg border">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Ekipman: </span>
                {operatorSelectorForEquipment.name}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Operatörlük Belgesi Ara</Label>
            <div className="relative">
              <Input
                placeholder="Belge adı ile ara..."
                value={operatorSearchQuery}
                onChange={(e) => setOperatorSearchQuery(e.target.value)}
                className="pr-10"
              />
              {operatorSearchQuery && (
                <button onClick={() => setOperatorSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto border rounded-lg">
            {filtered.map((op, idx) => (
              <label
                key={idx}
                className={`flex items-center gap-3 p-3 cursor-pointer transition-colors border-b last:border-0 ${selectedOperatorObj?.name === op.name ? 'bg-emerald-50 border-emerald-200' : 'hover:bg-slate-50'}`}
              >
                <input
                  type="radio"
                  name="operator"
                  checked={selectedOperatorObj?.name === op.name}
                  onChange={() => setSelectedOperatorObj(op)}
                  className="h-4 w-4 text-emerald-600"
                />
                <span className="text-sm font-medium">{op.name}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-500">Operatör belgesi bulunamadı</div>
            )}
          </div>
          {operatorSelectorStatus === 'onay' && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Geçerlilik Tarihi *</Label>
              <Input
                type="date"
                value={operatorValidityDate}
                onChange={(e) => setOperatorValidityDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={closeOperatorSelector}>İptal</Button>
          {selectedOperatorObj && (
            <Button
              type="button"
              onClick={() => handleOperatorSelection(selectedOperatorObj.name, operatorValidityDate)}
              disabled={!selectedOperatorObj || (operatorSelectorStatus === 'onay' && !operatorValidityDate)}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {operatorSelectorStatus === 'onay' ? 'Onayla' : 'Eksik Olarak İşaretle'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
